import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageMeta from '../components/PageMeta';
import FadeIn from '../components/FadeIn';

const galleryImages = [
  { src: 'https://picsum.photos/seed/ceremony1/280/420',   alt: 'Bride standing in soft morning light, Christchurch wedding', w: 280 },
  { src: 'https://picsum.photos/seed/couple1/210/420',     alt: 'Couple sharing first look in a garden setting, New Zealand', w: 210 },
  { src: 'https://picsum.photos/seed/bride1/280/420',      alt: 'Wedding ceremony on a New Zealand hillside', w: 280 },
  { src: 'https://picsum.photos/seed/engagement1/210/420', alt: 'Bride and groom walking through autumn trees, Canterbury', w: 210 },
  { src: 'https://picsum.photos/seed/reception1/280/420',  alt: 'Wedding reception detail and floral arrangements', w: 280 },
  { src: 'https://picsum.photos/seed/indian1/210/420',     alt: 'Indian wedding ceremony with vibrant floral mandap decor', w: 210 },
  { src: 'https://picsum.photos/seed/couple2/280/420',     alt: 'Engagement portrait in golden evening light, New Zealand', w: 280 },
  { src: 'https://picsum.photos/seed/portrait1/210/420',   alt: 'Wedding reception candid moment, Christchurch', w: 210 },
];

const homeServices = [
  {
    title: 'Wedding Photography',
    desc: 'Full-day documentary coverage that lets the real moments speak. Every look, every laugh, every tear preserved exactly as it was.',
  },
  {
    title: 'Wedding Videography',
    desc: 'Cinematic wedding films that bring your day back to life — the words, the music, the movement and all the feeling in between.',
  },
  {
    title: 'Engagement Shoots',
    desc: 'A relaxed session before the wedding day to tell the story of where you are right now, in the places that matter most to you.',
  },
  {
    title: 'Portrait Photography',
    desc: 'Thoughtful portraits for individuals, couples and families, made with warmth, patience, and a genuine desire to see you clearly.',
  },
  {
    title: 'Indian Weddings',
    desc: 'Specialising in the rich ceremony, colour and emotion of South Asian celebrations, from intimate family functions to multi-day events.',
  },
  {
    title: 'Corporate Photography',
    desc: 'Professional imagery for teams, events and brand stories — made with the same craft and care that goes into every personal project.',
  },
];

const testimonials = [
  {
    quote:
      'Dhrumil was the best decision we made for our wedding. He blended into our day completely, and the photos look like art. We still look at them every week.',
    name: 'Priya & James',
    detail: 'Wedding — Christchurch',
  },
  {
    quote:
      'Our engagement photos exceeded everything we imagined. Dhrumil has an incredible talent for capturing genuine feeling without it ever feeling forced.',
    name: 'Sarah & Michael',
    detail: 'Engagement — Wellington',
  },
  {
    quote:
      'From our Indian ceremony to the reception, Dhrumil was exceptional. The images are breathtaking and perfectly capture the emotion and colour of our celebration.',
    name: 'Ananya & Rohan',
    detail: 'Indian Wedding — Auckland',
  },
];

export default function Home() {
  const galleryRef = useRef(null);

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <PageMeta
        title="Dhrumil Desai Photography | Christchurch Wedding Photographer"
        description="Award-winning wedding and portrait photographer based in Christchurch, New Zealand. Capturing love stories with a cinematic, documentary approach. Available NZ wide."
      />

      {/* ── Hero ── */}
      <section className="min-h-screen flex flex-col justify-center items-center text-center px-6 bg-white pt-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut', delay: 0.1 }}
          className="max-w-3xl"
        >
          <p
            className="text-[10px] tracking-[0.4em] uppercase text-bronze mb-10 font-inter"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Christchurch, New Zealand
          </p>

          <h1
            className="font-playfair italic text-6xl md:text-7xl lg:text-8xl leading-[1.08] text-charcoal mb-8"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            Capturing Love.<br />Telling Stories.
          </h1>

          <p
            className="font-inter font-light text-base md:text-lg text-gray-500 mb-14 max-w-sm mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Wedding &amp; portrait photography across New Zealand,<br className="hidden md:block" />
            crafted with heart and intention.
          </p>

          <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
            <Link
              to="/portfolio"
              className="border border-charcoal px-10 py-3.5 text-[11px] tracking-[0.25em] uppercase font-inter text-charcoal hover:bg-charcoal hover:text-white transition-all duration-300"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              View Our Work
            </Link>
            <Link
              to="/contact"
              className="text-[11px] tracking-[0.25em] uppercase font-inter text-bronze border-b border-bronze pb-0.5 hover:text-charcoal hover:border-charcoal transition-colors duration-300"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Book a Session
            </Link>
          </div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <div className="flex flex-col items-center gap-2">
            <div className="w-px h-12 bg-gradient-to-b from-transparent to-gray-300" />
          </div>
        </motion.div>
      </section>

      {/* ── Divider ── */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <hr className="border-t border-gray-100" />
      </div>

      {/* ── Horizontal Scroll Gallery ── */}
      <section className="py-20 bg-white overflow-hidden" aria-label="Photo gallery">
        <FadeIn className="mb-12 px-6 md:px-16">
          <p
            className="text-[10px] tracking-[0.4em] uppercase text-bronze mb-3 font-inter"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Gallery
          </p>
          <h2
            className="font-playfair text-3xl md:text-4xl text-charcoal"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            Moments worth remembering
          </h2>
        </FadeIn>

        <div
          ref={galleryRef}
          className="overflow-hidden select-none px-6 md:px-16"
          style={{ cursor: 'grab' }}
        >
          <motion.div
            drag="x"
            dragConstraints={galleryRef}
            dragElastic={0.05}
            className="flex gap-4"
            style={{ width: 'max-content' }}
            whileTap={{ cursor: 'grabbing' }}
          >
            {galleryImages.map((img, i) => (
              <img
                key={i}
                src={img.src}
                alt={img.alt}
                className="flex-shrink-0 object-cover"
                style={{ width: img.w, height: 420 }}
                draggable="false"
                loading="lazy"
              />
            ))}
          </motion.div>
        </div>

        <FadeIn className="mt-10 px-6 md:px-16">
          <Link
            to="/portfolio"
            className="text-[11px] tracking-[0.25em] uppercase font-inter text-bronze border-b border-bronze pb-0.5 hover:text-charcoal hover:border-charcoal transition-colors duration-200"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            View Full Portfolio
          </Link>
        </FadeIn>
      </section>

      {/* ── Divider ── */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <hr className="border-t border-gray-100" />
      </div>

      {/* ── Services Grid ── */}
      <section className="py-24 px-6 md:px-12 bg-white" aria-labelledby="services-heading">
        <div className="max-w-7xl mx-auto">
          <FadeIn className="mb-16">
            <p
              className="text-[10px] tracking-[0.4em] uppercase text-bronze mb-3 font-inter"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              What We Offer
            </p>
            <h2
              id="services-heading"
              className="font-playfair text-3xl md:text-4xl text-charcoal"
              style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
            >
              Photography &amp; videography services
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
            {homeServices.map((s, i) => (
              <FadeIn key={s.title} delay={i * 0.08}>
                <h3
                  className="font-playfair text-xl text-charcoal mb-4"
                  style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
                >
                  {s.title}
                </h3>
                <p
                  className="font-inter font-light text-sm text-gray-500 leading-relaxed"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  {s.desc}
                </p>
              </FadeIn>
            ))}
          </div>

          <FadeIn className="mt-16">
            <Link
              to="/services"
              className="border border-charcoal px-10 py-3.5 text-[11px] tracking-[0.25em] uppercase font-inter text-charcoal hover:bg-charcoal hover:text-white transition-all duration-300 inline-block"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Explore All Services
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* ── About ── */}
      <section className="py-24 px-6 md:px-12 bg-cream" aria-labelledby="about-heading">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <FadeIn>
            <img
              src="https://picsum.photos/seed/photographer1/600/800"
              alt="Portrait of Dhrumil Desai, wedding photographer based in Christchurch, New Zealand"
              className="w-full aspect-[3/4] max-w-md object-cover"
              loading="lazy"
            />
          </FadeIn>

          {/* Text */}
          <FadeIn delay={0.15} className="max-w-lg">
            <p
              className="text-[10px] tracking-[0.4em] uppercase text-bronze mb-6 font-inter"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              About Dhrumil
            </p>
            <h2
              id="about-heading"
              className="font-playfair text-3xl md:text-4xl text-charcoal mb-8 leading-snug"
              style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
            >
              A photographer's eye.<br />A storyteller's soul.
            </h2>
            <div className="space-y-5">
              <p
                className="font-inter font-light text-sm md:text-base text-gray-600 leading-relaxed"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                Based in Christchurch and travelling throughout New Zealand, Dhrumil has spent years documenting the most meaningful chapters of people's lives — from intimate elopements in the Southern Alps to grand Indian wedding celebrations spanning multiple days.
              </p>
              <p
                className="font-inter font-light text-sm md:text-base text-gray-600 leading-relaxed"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                His approach is quiet and observant. He moves through your day as a guest, not a crew, capturing real emotion as it unfolds rather than staging moments that don't belong to you.
              </p>
              <p
                className="font-inter font-light text-sm md:text-base text-gray-600 leading-relaxed"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                Whether it's a morning bridal portrait lit by soft window light or two silhouettes dancing at the close of a wedding day, every image is made with care, intention, and deep respect for the story being told.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-block mt-10 border border-charcoal px-10 py-3.5 text-[11px] tracking-[0.25em] uppercase font-inter text-charcoal hover:bg-charcoal hover:text-white transition-all duration-300"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Work With Me
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="py-24 px-6 md:px-12 bg-white" aria-labelledby="testimonials-heading">
        <div className="max-w-7xl mx-auto">
          <FadeIn className="mb-16 text-center">
            <p
              className="text-[10px] tracking-[0.4em] uppercase text-bronze mb-3 font-inter"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Kind Words
            </p>
            <h2
              id="testimonials-heading"
              className="font-playfair text-3xl md:text-4xl text-charcoal"
              style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
            >
              What our couples say
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {testimonials.map((t, i) => (
              <FadeIn key={i} delay={i * 0.12}>
                <blockquote className="flex flex-col gap-6">
                  <p
                    className="font-playfair italic text-lg md:text-xl text-charcoal leading-relaxed"
                    style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
                  >
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <footer>
                    <div className="w-8 h-px bg-bronze mb-4" />
                    <p
                      className="font-inter text-sm text-charcoal tracking-wide"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                    >
                      {t.name}
                    </p>
                    <p
                      className="font-inter font-light text-xs text-gray-400 mt-1"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                    >
                      {t.detail}
                    </p>
                  </footer>
                </blockquote>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact CTA ── */}
      <section className="py-28 px-6 md:px-12 bg-cream text-center" aria-labelledby="cta-heading">
        <FadeIn className="max-w-xl mx-auto">
          <p
            className="text-[10px] tracking-[0.4em] uppercase text-bronze mb-6 font-inter"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Let's create something beautiful
          </p>
          <h2
            id="cta-heading"
            className="font-playfair text-3xl md:text-5xl text-charcoal mb-8 leading-tight"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            Ready to tell<br />your story?
          </h2>
          <p
            className="font-inter font-light text-sm text-gray-500 mb-12 leading-relaxed"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Get in touch to check availability for your wedding or portrait session anywhere in New Zealand.
          </p>
          <Link
            to="/contact"
            className="border border-charcoal px-12 py-4 text-[11px] tracking-[0.25em] uppercase font-inter text-charcoal hover:bg-charcoal hover:text-white transition-all duration-300 inline-block"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Get in Touch
          </Link>
        </FadeIn>
      </section>
    </motion.main>
  );
}
